/**
 * El perfil de un doctor, en dos pestañas separadas a propósito:
 *
 *   · **Perfil** — quién es: nombre, contacto, foto, consultorios asociados.
 *   · **Facturación** — cómo factura y dónde es pagador. Un doctor puede
 *     pagar como persona física o a través de una sociedad, así que su
 *     nombre y su razón social son datos distintos y no se asume que
 *     coincidan; y ser pagador no es una propiedad global suya, es una
 *     posición en la distribución de un contrato concreto, con sus fechas.
 *
 * La identidad personal y la identidad fiscal no se mezclan en la misma
 * pantalla.
 */

import { useState } from 'react';
import { Icon } from '@/components/common/NavIcons';
import { Link, useParams } from 'react-router-dom';
import {
  ALLOCATION_MODE_LABEL, cedulaLabel, dateLabel, PAYMENT_STATUS_LABEL, PAYMENT_STATUS_TONE,
  periodLabel, PREFERRED_CHANNEL, TAX_ID_TYPE, type AllocationMode, type PaymentStatus,
  type PreferredChannel, type TaxIdType,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import {
  Async, Badge, Button, Card, Cell, Empty, Field, Modal, Money, Note, PageHead, StatusBadge, Table,
} from '@/components/ui';
import { PhotoUploader } from '@/components/common/PhotoUploader';
import { FiscalImportDropzone } from '@/components/common/FiscalImportDropzone';
import { InviteButton } from '@/components/common/InviteButton';
import type { BillingPrefill } from '@/lib/fiscalImport';

/** No hay mapa de etiquetas en `@cleanblue/shared` para este enum todavía. */
const TAX_ID_TYPE_LABEL: Record<TaxIdType, string> = {
  FISICA: 'Cédula física',
  JURIDICA: 'Cédula jurídica',
  DIMEX: 'DIMEX',
  NITE: 'NITE',
};

const PREFERRED_CHANNEL_LABEL: Record<PreferredChannel, string> = {
  EMAIL: 'Correo',
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
};

interface DoctorDetail {
  profile: {
    id: string; full_name: string; email: string | null; phone: string | null;
    preferred_channel: string; is_active: number; notes: string | null; photo_key: string | null;
  };
  party: { id: string; display_name: string } | null;
  billing_profiles: {
    id: string; legal_name: string; tax_id: string | null; tax_id_type: string | null;
    billing_email: string | null; phone: string | null; address: string | null;
    is_default: number; is_active: number; invoice_required: number;
  }[];
  billing_ready: boolean;
  billing_missing: string[];
  /** Ya entró y eligió su contraseña. Una temporal sin usar no cuenta. */
  account_active: boolean;
  memberships: {
    id: string; service_unit_id: string; unit_code: string; unit_name: string | null;
    location_name: string | null; role: string; active_from: string; active_until: string | null;
  }[];
  responsibilities: {
    subscription_id: string; unit_code: string; service_name: string; mode: AllocationMode;
    effective_from: string; effective_to: string | null; confirmed: number;
    percentage: number | null; fixed_amount_minor: number | null; rotation_seq: number | null;
  }[];
  recent_charges: {
    id: string; period_start: string; due_minor: number; paid_minor: number;
    payment_status: PaymentStatus; unit_code: string;
  }[];
}

export function DoctorProfilePage() {
  const { id } = useParams<{ id: string }>();
  const query = useApi<DoctorDetail>(`/admin/profiles/${id}`);
  const [tab, setTab] = useState<'perfil' | 'facturacion'>('perfil');
  const [addingBilling, setAddingBilling] = useState(false);
  const [copiedTaxId, setCopiedTaxId] = useState<string | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [prefillBilling, setPrefillBilling] = useState<BillingPrefill | null>(null);

  const toggleInvoiceRequired = useApiMutation(
    (vars: { profileId: string; invoiceRequired: boolean }) =>
      api.patch(`/admin/billing-profiles/${vars.profileId}/invoice-required`, { invoice_required: vars.invoiceRequired }),
    [`/admin/profiles/${id}`],
  );

  return (
    <Async query={query}>
      {(data) => (
        <>
          <PageHead
            title={data.profile.full_name}
            subtitle={
              <>
                {data.profile.email ?? 'Sin correo'}
                {data.profile.phone && ` · ${data.profile.phone}`}
                {' · '}
                {data.billing_ready
                  ? <Badge tone="ok">Facturación completa</Badge>
                  : <Badge tone="warn">Facturación incompleta</Badge>}
              </>
            }
            actions={<Link to="/admin/clientes?tab=doctores" className="btn" title="Volver" aria-label="Volver a Doctores"><Icon name="volver" /></Link>}
          />

          <div className="tabs mb-2">
            <button
              type="button" className={`tab${tab === 'perfil' ? ' is-active' : ''}`} aria-pressed={tab === 'perfil'}
              onClick={() => { if (tab !== 'perfil') { haptic.light(); setTab('perfil'); } }} // HAPTIC_FEEDBACK
            >
              Perfil
            </button>
            <button
              type="button" className={`tab${tab === 'facturacion' ? ' is-active' : ''}`} aria-pressed={tab === 'facturacion'}
              onClick={() => { if (tab !== 'facturacion') { haptic.light(); setTab('facturacion'); } }} // HAPTIC_FEEDBACK
            >
              Facturación
              {!data.billing_ready && <Badge tone="warn" plain> !</Badge>}
            </button>
          </div>

          {tab === 'perfil' ? (
            <div className="grid grid--sidebar">
              <div style={{ display: 'grid', gap: 14 }}>
                <Card
                  title="Datos personales"
                  actions={<Button onClick={() => setEditingProfile(true)}>Editar perfil</Button>}
                >
                  <PhotoUploader
                    uploadPath={`/admin/profiles/${data.profile.id}/photo`}
                    deletePath={`/admin/profiles/${data.profile.id}/photo`}
                    photoSrc={data.profile.photo_key
                      ? `/api/admin/profiles/${data.profile.id}/photo?v=${data.profile.photo_key}` : null}
                    name={data.profile.full_name}
                    invalidate={[`/admin/profiles/${data.profile.id}`]}
                  />
                  <dl className="dl mt-2">
                    <dt>Correo</dt><dd>{data.profile.email ?? '—'}</dd>
                    <dt>Teléfono</dt><dd>{data.profile.phone ?? '—'}</dd>
                    <dt>Canal preferido</dt>
                    <dd>{PREFERRED_CHANNEL_LABEL[data.profile.preferred_channel as PreferredChannel]
                      ?? data.profile.preferred_channel}</dd>
                  </dl>
                  {data.profile.notes && <p className="small muted mt-2">{data.profile.notes}</p>}

                  {/* Un solo camino para habilitar a alguien, el mismo que la
                      galería: se le manda el enlace y elige su contraseña. La
                      contraseña temporal hacía lo mismo por otra puerta y
                      obligaba a dictar una clave que el sistema iba a pedir
                      cambiar en el primer ingreso.

                      Solo aparece mientras no haya entrado: `account_active`
                      mira `must_change = 0`, no si existe credencial — una
                      temporal vieja deja fila y escondía este botón para
                      alguien que nunca entró. */}
                  {!data.account_active && (
                    <div className="mt-2">
                      {data.profile.email ? (
                        <InviteButton
                          profileId={data.profile.id}
                          personName={data.profile.full_name}
                          email={data.profile.email}
                        />
                      ) : (
                        <p className="small muted">
                          Cargale un correo para poder invitarla: es su nombre de usuario.
                        </p>
                      )}
                    </div>
                  )}
                </Card>

                {data.recent_charges.length > 0 && (
                  <Card title="Sus cobros" flush>
                    <Table head={
                      <tr>
                        <th>Período</th><th>Consultorio</th><th className="right">Debe</th><th>Estado</th>
                      </tr>
                    }>
                      {data.recent_charges.map((ch) => (
                        <tr key={ch.id}>
                          <td>{periodLabel(ch.period_start)}</td>
                          <td className="muted">{ch.unit_code}</td>
                          <td className="right">
                            <Cell sub={ch.paid_minor > 0
                              ? <>pagó <Money value={ch.paid_minor} /></>
                              : undefined}>
                              <Money value={ch.due_minor} />
                            </Cell>
                          </td>
                          <td>
                            <StatusBadge
                              value={ch.payment_status}
                              labels={PAYMENT_STATUS_LABEL}
                              tones={PAYMENT_STATUS_TONE}
                            />
                          </td>
                        </tr>
                      ))}
                    </Table>
                  </Card>
                )}
              </div>

              <Card title="Consultorios">
                {/* Con vigencia: es lo que decide en qué meses podía recibir cobros.
                    Las cerradas se muestran atenuadas, no se ocultan — el histórico
                    explica por qué hay cobros suyos en meses en los que ya no está. */}
                {data.memberships.length === 0 ? (
                  <p className="muted small">No está asociado a ningún consultorio.</p>
                ) : (
                  <div style={{ display: 'grid', gap: 12 }}>
                    {data.memberships.map((m) => (
                      <div key={m.id} style={m.active_until ? { opacity: 0.6 } : undefined}>
                        <Link to={`/admin/consultorios/${m.service_unit_id}`} className="strong">
                          Consultorio {m.unit_code}
                        </Link>
                        <span className="tbl__sub">
                          {m.location_name ?? ''}
                          {m.role === 'UNIT_ADMIN' && ' · administra'}
                        </span>
                        <span className="tbl__sub">
                          {m.active_until
                            ? `${dateLabel(m.active_from)} → ${dateLabel(m.active_until)}`
                            : `Desde ${dateLabel(m.active_from)}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 14 }}>
              {!data.billing_ready && (
                <Note tone="warn">
                  <strong>Falta para poder facturarle:</strong>{' '}
                  {data.billing_missing.join(' · ')}. Se le puede cobrar igual; lo que no sale es
                  la factura electrónica.
                </Note>
              )}

              <Card
                title="Identidades fiscales"
                flush
                actions={data.party && data.billing_profiles.length > 0 && (
                  <Button onClick={() => setAddingBilling(true)}>Añadir identidad fiscal</Button>
                )}
              >
                {data.billing_profiles.length === 0 ? (
                  <div style={{ display: 'grid', gap: 14, padding: '4px 4px 14px' }}>
                    <Empty
                      icon="documento"
                      title="Sin perfiles de facturación"
                      hint="Creá uno manualmente o cargá una factura existente para extraer los datos."
                    />
                    {data.party && (
                      <FiscalImportDropzone
                        onParsed={(fields) => { setPrefillBilling(fields); setAddingBilling(true); }}
                        onManual={() => setAddingBilling(true)}
                      />
                    )}
                  </div>
                ) : (
                  <Table head={
                    <tr><th>Razón social</th><th>Identificación</th><th>Correo</th><th>Factura</th><th /></tr>
                  }>
                    {data.billing_profiles.map((b) => (
                      <tr key={b.id}>
                        <td className="strong">
                          <Cell sub={b.address}>{b.legal_name}</Cell>
                        </td>
                        <td className="small mono cedula">
                          <Cell sub={b.tax_id_type ?? undefined}>
                            {b.tax_id_type === 'JURIDICA' && b.tax_id ? (
                              <button
                                type="button"
                                className="tax-id-copy"
                                title="Copiar identificación solo con números"
                                aria-label={`Copiar identificación jurídica ${cedulaLabel(b.tax_id, b.tax_id_type)}`}
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(b.tax_id!.replace(/\D/g, ''));
                                    setCopiedTaxId(b.id);
                                    window.setTimeout(() => setCopiedTaxId((id) => id === b.id ? null : id), 1600);
                                  } catch {
                                    setCopiedTaxId(null);
                                  }
                                }}
                              >
                                <span>{cedulaLabel(b.tax_id, b.tax_id_type)}</span>
                                <svg aria-hidden="true" viewBox="0 0 24 24" className="tax-id-copy__icon">
                                  {copiedTaxId === b.id ? (
                                    <path d="m5 12 4 4L19 6" />
                                  ) : (
                                    <><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" /></>
                                  )}
                                </svg>
                                <span className="tax-id-copy__hint" aria-live="polite">
                                  {copiedTaxId === b.id ? 'Copiado' : 'Copiar'}
                                </span>
                              </button>
                            ) : cedulaLabel(b.tax_id, b.tax_id_type) ?? '—'}
                          </Cell>
                        </td>
                        <td className="small muted">{b.billing_email ?? '—'}</td>
                        <td className="small">
                          <label className="row" style={{ gap: 6, cursor: 'pointer' }} title="Si esta persona necesita factura fiscal por sus cobros (desmarcá para excepciones sin IVA, ej. montos fijos negociados sin impuesto).">
                            <input
                              type="checkbox"
                              checked={b.invoice_required === 1}
                              disabled={toggleInvoiceRequired.isPending}
                              onChange={(e) => {
                                haptic.light(); // HAPTIC_FEEDBACK
                                toggleInvoiceRequired.mutate({ profileId: b.id, invoiceRequired: e.target.checked });
                              }}
                            />
                            <span className="muted">{b.invoice_required === 1 ? 'Requiere' : 'Sin IVA'}</span>
                          </label>
                        </td>
                        <td className="right">
                          {b.is_default === 1 && <Badge tone="info" plain>Por defecto</Badge>}
                        </td>
                      </tr>
                    ))}
                  </Table>
                )}
              </Card>

              <Card title="Dónde es pagador" flush>
                {data.responsibilities.length === 0 ? (
                  <Empty icon="cobros" title="No participa en ningún reparto" />
                ) : (
                  <Table head={
                    <tr>
                      <th>Consultorio</th><th>Servicio</th><th>Reparto</th><th className="right">Su parte</th>
                    </tr>
                  }>
                    {data.responsibilities.map((r) => (
                      <tr
                        key={`${r.subscription_id}-${r.effective_from}`}
                        style={r.effective_to ? { opacity: 0.55 } : undefined}
                      >
                        <td className="strong">
                          <Link to={`/admin/contratos/${r.subscription_id}`}>
                            Consultorio {r.unit_code}
                          </Link>
                        </td>
                        <td>{r.service_name}</td>
                        <td>
                          <Cell sub={r.effective_to
                            ? `Hasta ${dateLabel(r.effective_to)}`
                            : `Desde ${dateLabel(r.effective_from)}`}>
                            {ALLOCATION_MODE_LABEL[r.mode] ?? r.mode}
                            {!r.confirmed && <Badge tone="warn" plain> sin confirmar</Badge>}
                          </Cell>
                        </td>
                        <td className="right">
                          {r.percentage != null ? `${r.percentage}%`
                            : r.fixed_amount_minor != null ? <Money value={r.fixed_amount_minor} />
                              : r.rotation_seq != null ? `Turno ${r.rotation_seq + 1}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </Table>
                )}
              </Card>
            </div>
          )}

          {editingProfile && (
            <EditDoctorProfileModal
              profile={data.profile}
              onClose={() => setEditingProfile(false)}
            />
          )}

          {addingBilling && data.party && (
            <AddBillingProfileModal
              profileId={data.profile.id}
              partyId={data.party.id}
              defaultLegalName={data.profile.full_name}
              defaultEmail={data.profile.email}
              prefill={prefillBilling}
              onClose={() => { setAddingBilling(false); setPrefillBilling(null); }}
            />
          )}
        </>
      )}
    </Async>
  );
}

/**
 * Edita los datos personales ya existentes. Reutiliza `profileInput.partial()`
 * vía `PATCH /admin/profiles/:id`, el mismo endpoint que ya mantiene
 * `billing_parties.display_name` en sincronía con el nombre.
 */
function EditDoctorProfileModal({ profile, onClose }: {
  profile: DoctorDetail['profile'];
  onClose: () => void;
}) {
  const [fullName, setFullName] = useState(profile.full_name);
  const [email, setEmail] = useState(profile.email ?? '');
  const [phone, setPhone] = useState(profile.phone ?? '');
  const [channel, setChannel] = useState<PreferredChannel>(
    (profile.preferred_channel as PreferredChannel) ?? 'WHATSAPP',
  );

  const save = useApiMutation(
    () => api.patch(`/admin/profiles/${profile.id}`, {
      full_name: fullName.trim(),
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      preferred_channel: channel,
    }),
    [`/admin/profiles/${profile.id}`],
    { onSuccess: onClose },
  );

  const valid = fullName.trim().length > 0;

  return (
    <Modal
      title="Editar perfil"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      {save.error && <Note tone="danger">{save.error.message}</Note>}

      <Field label="Nombre completo" required>
        <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} autoFocus />
      </Field>

      <Field label="Correo" hint="Es su usuario para entrar al portal.">
        <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>

      <Field label="Teléfono" hint="WhatsApp, si es distinto del teléfono del consultorio.">
        <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </Field>

      <Field label="Canal preferido">
        <select className="select" value={channel} onChange={(e) => setChannel(e.target.value as PreferredChannel)}>
          {PREFERRED_CHANNEL.map((c) => (
            <option key={c} value={c}>{PREFERRED_CHANNEL_LABEL[c]}</option>
          ))}
        </select>
      </Field>
    </Modal>
  );
}

/**
 * El alta de una identidad fiscal.
 *
 * `billing_ready` y el faltante del `Note` de arriba son calculados por el
 * Worker, no acá: guardar solo invalida `/admin/profiles/:id` y la pantalla
 * se refresca sola con lo que el servidor decida.
 *
 * `prefill` viene de leer un XML/PDF (`FiscalImportDropzone`): solo rellena
 * campos, nunca guarda por sí solo — el usuario siempre revisa y confirma acá.
 */
function AddBillingProfileModal({
  profileId, partyId, defaultLegalName, defaultEmail, prefill, onClose,
}: {
  profileId: string;
  partyId: string;
  defaultLegalName: string;
  defaultEmail: string | null;
  prefill: BillingPrefill | null;
  onClose: () => void;
}) {
  const [taxIdType, setTaxIdType] = useState<TaxIdType>(prefill?.taxIdType ?? 'FISICA');
  const [taxId, setTaxId] = useState(prefill?.taxId ?? '');
  const [legalName, setLegalName] = useState(prefill?.legalName || defaultLegalName);
  const [billingEmail, setBillingEmail] = useState(prefill?.billingEmail || defaultEmail || '');
  const [phone, setPhone] = useState(prefill?.phone ?? '');
  const [address, setAddress] = useState(prefill?.address ?? '');

  const save = useApiMutation(
    () => api.post('/admin/billing-profiles', {
      party_id: partyId,
      tax_id_type: taxIdType,
      tax_id: taxId.trim(),
      legal_name: legalName.trim(),
      billing_email: billingEmail.trim() || undefined,
      phone: phone.trim() || undefined,
      address: address.trim() || undefined,
    }),
    [`/admin/profiles/${profileId}`],
    { onSuccess: onClose },
  );

  const valid = taxId.trim().length > 0 && legalName.trim().length > 0;

  return (
    <Modal
      title="Añadir identidad fiscal"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      {save.error && <Note tone="danger">{save.error.message}</Note>}
      {prefill && (
        <Note tone="info">
          Prellenado desde el archivo cargado. Revisá los datos antes de guardar.
        </Note>
      )}

      <Field label="Tipo de identificación" required>
        <select
          className="select"
          value={taxIdType}
          onChange={(e) => setTaxIdType(e.target.value as TaxIdType)}
        >
          {TAX_ID_TYPE.map((t) => (
            <option key={t} value={t}>{TAX_ID_TYPE_LABEL[t]}</option>
          ))}
        </select>
      </Field>

      <Field label="Número" required>
        <input className="input" value={taxId} onChange={(e) => setTaxId(e.target.value)} autoFocus />
      </Field>

      <Field label="Nombre o razón social" required>
        <input className="input" value={legalName} onChange={(e) => setLegalName(e.target.value)} />
      </Field>

      <Field label="Correo para factura electrónica">
        <input
          className="input" type="email" value={billingEmail}
          onChange={(e) => setBillingEmail(e.target.value)}
        />
      </Field>

      <Field label="Teléfono">
        <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </Field>

      <Field label="Dirección">
        <input className="input" value={address} onChange={(e) => setAddress(e.target.value)} />
      </Field>
    </Modal>
  );
}
